import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Copy, QrCode, CheckCircle2, ArrowLeft, Loader2, ShieldCheck, DollarSign, ChevronRight, Upload, Send } from "lucide-react";

const pathFromUrl = (u) => (u || "").replace("/api/files/", "");

export default function Pagamento() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metodo, setMetodo] = useState(null); // null = tela de escolha
  const [parcelas, setParcelas] = useState(1);
  const [valorInput, setValorInput] = useState("");
  const [opcoesCustom, setOpcoesCustom] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [compOpen, setCompOpen] = useState(false);
  const [compArquivo, setCompArquivo] = useState(null);
  const [compValor, setCompValor] = useState("");
  const [compObs, setCompObs] = useState("");
  const [compEnviando, setCompEnviando] = useState(false);
  const [compEnviado, setCompEnviado] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/orcamento/${token}/pagamento`);
      setInfo(data);
    } catch {
      setInfo({ notFound: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Recalcula opções quando usuário digita um valor parcial
  const valorNum = useMemo(() => {
    const v = parseFloat((valorInput || "").replace(",", "."));
    return isNaN(v) || v <= 0 ? null : v;
  }, [valorInput]);

  useEffect(() => {
    if (!info?.saldo) return;
    if (!valorNum || Math.abs(valorNum - info.saldo) < 0.01) {
      setOpcoesCustom(null);
      return;
    }
    // Recalcula opções para o valor digitado (mesma fórmula do backend)
    const taxaFixa = info.opcoes_parcelamento?.[0]?.taxa_fixa ?? 0;
    const base = Math.min(valorNum, info.saldo);
    const list = (info.opcoes_parcelamento || []).map((o) => {
      const total = Math.round((base * (1 + o.taxa_pct / 100) + taxaFixa) * 100) / 100;
      const parcela = Math.round((total / o.parcelas) * 100) / 100;
      return { ...o, total, parcela };
    });
    setOpcoesCustom(list);
  }, [valorNum, info?.saldo]);

  if (loading)
    return <div className="min-h-screen flex items-center justify-center text-zinc-500">Carregando pagamento...</div>;

  if (info?.notFound)
    return <Centered title="Pagamento indisponível" subtitle="Link inválido ou expirado." />;

  if (!info.liberado && info.status !== "pago") {
    return (
      <Centered
        title="Pagamento aguardando aprovação"
        subtitle={`Assim que o cliente aprovar o orçamento, o pagamento ficará disponível. Fale com a ${info.empresa?.nome_empresa || "BBH"} em caso de dúvida.`}
        token={token}
      />
    );
  }

  if (info.status === "pago") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: "#FAFAFA" }}>
        <div className="max-w-md">
          <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
          <div className="font-heading text-2xl font-bold mb-2">Orçamento quitado</div>
          <p className="text-zinc-500 mb-6">
            Total pago: <strong>{money(info.total_pago)}</strong>.
          </p>
          <Link to={`/q/${token}`} data-testid="pagto-voltar" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bbh-btn-primary text-sm font-semibold">
            <ArrowLeft className="w-4 h-4" /> Voltar ao orçamento
          </Link>
        </div>
      </div>
    );
  }

  const opcoes = opcoesCustom || info.opcoes_parcelamento || [];
  const escolhida = opcoes.find((o) => o.parcelas === parcelas) || opcoes[0];
  const valorBase = valorNum || info.saldo;

  const pagarCartao = async () => {
    if (redirecting) return;
    setRedirecting(true);
    try {
      const origin = window.location.origin;
      const body = { parcelas, origin_url: origin };
      if (valorNum && Math.abs(valorNum - info.saldo) >= 0.01) body.valor = valorNum;
      const { data } = await api.post(`/public/orcamento/${token}/pagamento/checkout`, body);
      window.location.href = data.checkout_url;
    } catch (e) {
      setRedirecting(false);
      toast.error(e.response?.data?.detail || "Não foi possível iniciar o pagamento");
    }
  };

  const copiarPix = () => {
    navigator.clipboard.writeText(info.pix?.chave || "");
    toast.success("Chave PIX copiada!");
  };

  const enviarComprovante = async () => {
    if (compEnviando) return;
    const v = parseFloat((compValor || "").replace(",", "."));
    if (!v || v <= 0) { toast.error("Informe o valor pago"); return; }
    if (!compArquivo) { toast.error("Anexe o comprovante (foto ou PDF)"); return; }
    setCompEnviando(true);
    try {
      const fd = new FormData();
      fd.append("arquivo", compArquivo);
      fd.append("valor", String(v));
      fd.append("observacao", compObs || "");
      await api.post(`/public/orcamento/${token}/comprovante-pix`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCompEnviado(true);
      toast.success("Comprovante enviado! A BBH vai conferir e dar baixa.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar comprovante");
    } finally { setCompEnviando(false); }
  };

  const temPix = !!(info.pix?.chave || info.pix?.qr_code);
  const pixQr = info.pix?.qr_code ? fileUrl(pathFromUrl(info.pix.qr_code)) : null;
  const pagouAntes = (info.pagamentos || []).length > 0;

  return (
    <div className="min-h-screen pb-10" style={{ background: "#FAFAFA" }} data-testid="pagamento-page">
      <header className="px-5 py-4" style={{ background: "#14243D", color: "#fff" }}>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to={`/q/${token}`} data-testid="pagto-back" className="inline-flex items-center gap-1.5 text-sm" style={{ color: "#fff" }}>
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Link>
          <div className="text-xs opacity-80">{info.empresa?.nome_empresa}</div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 space-y-5">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">BBH Pagamento</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Pagar orçamento {info.numero}</h1>
          <div className="mt-1 text-zinc-600 text-sm">
            Valor total: <strong>{money(info.valor_total)}</strong>
            {pagouAntes && <> · já pago <strong className="text-emerald-700">{money(info.total_pago)}</strong></>}
          </div>
          {info.status === "parcial" && (
            <div className="mt-2 rounded-md border p-2.5 text-sm" style={{ background: "#F0FDF4", borderColor: "#BBF7D0", color: "#065F46" }}>
              Saldo devedor: <strong>{money(info.saldo)}</strong>
            </div>
          )}
        </div>

        {/* Valor a pagar (parcial opcional) */}
        <div className="bbh-card p-4" data-testid="bloco-valor">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Quanto quer pagar agora?</label>
          <div className="mt-1 flex items-stretch gap-2">
            <div className="flex items-center gap-1 flex-1 rounded-md border border-zinc-200 bg-white px-3">
              <span className="text-zinc-500 text-sm">R$</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder={info.saldo.toFixed(2).replace(".", ",")}
                value={valorInput}
                onChange={(e) => setValorInput(e.target.value.replace(/[^\d.,]/g, ""))}
                data-testid="input-valor-pagto"
                className="flex-1 py-2 bg-transparent outline-none text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => setValorInput(info.saldo.toFixed(2).replace(".", ","))}
              data-testid="btn-pagar-total"
              className="px-3 py-2 rounded-md border border-zinc-200 bg-white text-xs font-medium hover:bg-zinc-50"
            >
              Pagar total
            </button>
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">
            Deixe em branco para pagar o saldo completo ({money(info.saldo)}).
          </div>
        </div>

        {/* Tela de escolha de método */}
        {metodo === null ? (
          <div className="space-y-3" data-testid="escolha-metodo">
            <div className="text-sm text-zinc-600">Como você quer pagar?</div>
            {temPix && (
              <button
                onClick={() => setMetodo("pix")}
                data-testid="escolher-pix"
                className="w-full flex items-center justify-between gap-3 p-4 rounded-lg border bg-white hover:brightness-[0.98] transition text-left"
                style={{ borderColor: "#e4e4e7" }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "#32BCAD", color: "#fff" }}>
                    <QrCode className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-heading text-lg font-bold leading-tight">PIX</div>
                    <div className="text-xs text-zinc-500">Pagamento à vista sem acréscimo — <strong>{money(valorBase)}</strong></div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-400" />
              </button>
            )}
            {info.cartao_ativo ? (
            <button
              onClick={() => setMetodo("cartao")}
              data-testid="escolher-cartao"
              className="w-full flex items-center justify-between gap-3 p-4 rounded-lg border transition text-left"
              style={{ background: "#14243D", borderColor: "#14243D", color: "#fff" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.14)" }}>
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading text-lg font-bold leading-tight">Cartão de crédito</div>
                  <div className="text-xs" style={{ color: "rgba(255,255,255,0.8)" }}>Em até 6x — juros do parcelamento por conta do cliente</div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5" style={{ color: "rgba(255,255,255,0.7)" }} />
            </button>
            ) : (
              <div
                data-testid="cartao-em-implantacao"
                aria-disabled="true"
                className="w-full flex items-center gap-3 p-4 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 text-zinc-400 cursor-not-allowed"
              >
                <div className="w-11 h-11 rounded-full flex items-center justify-center bg-zinc-200">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading text-lg font-bold leading-tight">Cartão de crédito</div>
                  <div className="text-xs">Pagamento por cartão em implantação</div>
                </div>
              </div>
            )}
            {!temPix && (
              <div className="text-[11px] text-zinc-500 text-center">
                PIX indisponível no momento — chave não configurada pelo administrador.
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setMetodo(null)}
            data-testid="voltar-metodos"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-800"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Trocar forma de pagamento
          </button>
        )}

        {metodo === "cartao" && info.cartao_ativo && (
          <section className="bbh-card p-5 space-y-4" data-testid="bloco-cartao">
            <div className="text-sm text-zinc-600">
              Escolha o parcelamento. <strong>Os juros do parcelamento são de responsabilidade do cliente</strong>, para que a BBH receba o valor integral da mão de obra.
            </div>

            <div className="space-y-2">
              {opcoes.map((o) => (
                <button
                  key={o.parcelas}
                  onClick={() => setParcelas(o.parcelas)}
                  data-testid={`parcela-${o.parcelas}x`}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-md border text-sm transition"
                  style={parcelas === o.parcelas ? { borderColor: "#14243D", background: "rgba(20,36,61,0.05)" } : { borderColor: "#e4e4e7", background: "#fff" }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full border-2" style={parcelas === o.parcelas ? { borderColor: "#14243D", background: "#14243D" } : { borderColor: "#d4d4d8" }} />
                    <div className="text-left">
                      <div className="font-semibold">
                        {o.parcelas}x de {money(o.parcela)}
                      </div>
                      <div className="text-[11px] text-zinc-500">
                        {o.sem_juros ? "à vista" : `${o.taxa_pct}% de acréscimo`}
                        {o.taxa_fixa ? ` + R$ ${o.taxa_fixa.toFixed(2).replace(".", ",")} fixa` : ""}
                        {" "}— total {money(o.total)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="pt-2 border-t border-zinc-100">
              <div className="flex items-baseline justify-between">
                <div className="text-xs text-zinc-500">Total a pagar agora</div>
                <div className="font-heading text-2xl font-bold" style={{ color: "#14243D" }} data-testid="total-cartao">
                  {money(escolhida?.total || 0)}
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 text-right">
                Valor que entra no orçamento: {money(valorBase)}
              </div>
            </div>

            <button
              onClick={pagarCartao}
              disabled={redirecting}
              data-testid="pagar-cartao-btn"
              className="w-full py-3 rounded-md text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: "#059669" }}
            >
              {redirecting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Redirecionando...</>) : (<><CreditCard className="w-4 h-4" /> Pagar {parcelas}x</>)}
            </button>

            <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 justify-center">
              <ShieldCheck className="w-3.5 h-3.5" /> Pagamento processado com segurança pela Stripe.
            </div>
          </section>
        )}

        {metodo === "pix" && (
          <section className="bbh-card p-5 space-y-4" data-testid="bloco-pix">
            <div className="text-sm text-zinc-600">
              Pague diretamente ao {info.empresa?.nome_empresa || "titular"} via PIX. Valor a pagar agora:{" "}
              <strong>{money(valorBase)}</strong> (sem acréscimo).
            </div>

            {pixQr && (
              <div className="flex justify-center">
                <img src={pixQr} alt="QR Code PIX" className="w-56 h-56 object-contain rounded-md border border-zinc-200" data-testid="pix-qrcode" />
              </div>
            )}

            {info.pix?.chave && (
              <div className="space-y-1">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
                  Chave PIX {info.pix?.tipo_chave ? `(${info.pix.tipo_chave})` : ""}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-3 py-2.5 rounded-md bg-zinc-50 border border-zinc-200 text-sm break-all" data-testid="pix-chave">
                    {info.pix.chave}
                  </div>
                  <button onClick={copiarPix} data-testid="copiar-pix-btn" className="shrink-0 px-3 py-2.5 rounded-md bg-white border border-zinc-200 text-sm font-medium hover:bg-zinc-50 flex items-center gap-1.5">
                    <Copy className="w-4 h-4" /> Copiar
                  </button>
                </div>
              </div>
            )}

            {info.pix?.titular && (
              <div className="text-xs text-zinc-500">Titular: <strong>{info.pix.titular}</strong></div>
            )}

            <div className="text-[11px] text-amber-900 rounded-md p-2.5" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
              Após pagar, envie o comprovante abaixo. A BBH recebe uma notificação e dá baixa no app.
            </div>

            {/* Envio de comprovante */}
            {compEnviado ? (
              <div className="rounded-md p-3 text-sm text-emerald-900" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }} data-testid="comprovante-enviado">
                <CheckCircle2 className="w-4 h-4 inline mr-1" /> Comprovante enviado com sucesso! Aguarde a confirmação da BBH.
              </div>
            ) : (
              <div className="rounded-md border border-zinc-200 p-3" data-testid="bloco-comprovante">
                <div className="font-semibold text-sm mb-2 flex items-center gap-1.5">
                  <Upload className="w-4 h-4" /> Enviar comprovante do PIX
                </div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Valor pago</label>
                <div className="flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-3 mt-1">
                  <span className="text-zinc-500 text-sm">R$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={compValor}
                    onChange={(e) => setCompValor(e.target.value.replace(/[^\d.,]/g, ""))}
                    placeholder={valorBase.toFixed(2).replace(".", ",")}
                    data-testid="comprovante-valor"
                    className="flex-1 py-2 bg-transparent outline-none text-sm"
                  />
                </div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mt-2 block">
                  Arquivo (foto ou PDF)
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    id="comp-file"
                    data-testid="comprovante-arquivo"
                    onChange={(e) => setCompArquivo(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  <label
                    htmlFor="comp-file"
                    className="flex-1 px-3 py-2 rounded-md border border-dashed border-zinc-300 text-sm text-zinc-600 text-center cursor-pointer hover:border-zinc-400 bg-white"
                  >
                    {compArquivo ? compArquivo.name : "Escolher arquivo..."}
                  </label>
                </div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mt-2 block">
                  Observação (opcional)
                </label>
                <input
                  type="text"
                  value={compObs}
                  onChange={(e) => setCompObs(e.target.value)}
                  placeholder="Ex: parcela 2 de 3"
                  data-testid="comprovante-obs"
                  className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bg-white text-sm"
                />
                <button
                  onClick={enviarComprovante}
                  disabled={compEnviando || !compArquivo}
                  data-testid="enviar-comprovante-btn"
                  className="mt-3 w-full py-2.5 rounded-md text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: "#14243D" }}
                >
                  {compEnviando ? (<><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>) : (<><Send className="w-4 h-4" /> Enviar comprovante</>)}
                </button>
              </div>
            )}
          </section>
        )}

        {/* Histórico pagamentos */}
        {pagouAntes && (
          <div className="bbh-card p-4" data-testid="historico-pagamentos">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">Pagamentos já realizados</div>
            <ul className="space-y-1.5 text-sm">
              {info.pagamentos.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-zinc-700">
                    <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
                    {money(p.valor)} <span className="text-zinc-500 text-xs">· {p.metodo === "pix" ? "PIX" : `Cartão ${p.parcelas}x`}</span>
                  </span>
                  <span className="text-xs text-zinc-500">
                    {p.data ? new Date(p.data).toLocaleDateString("pt-BR") : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}

function Centered({ title, subtitle, token }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: "#FAFAFA" }}>
      <div className="max-w-md">
        <div className="font-heading text-2xl font-bold mb-2">{title}</div>
        <p className="text-zinc-500 mb-6">{subtitle}</p>
        {token && (
          <Link to={`/q/${token}`} data-testid="pagto-voltar" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bbh-btn-primary text-sm font-semibold">
            <ArrowLeft className="w-4 h-4" /> Voltar ao orçamento
          </Link>
        )}
      </div>
    </div>
  );
}
